import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faCoins } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoginMenu from 'components/ApiAuthorization/LoginMenu';
import IconButton from 'components/ui/Button/IconButton';
import Flex from 'components/ui/Flex';
import { Search } from 'components/ui/Search';
import useAppSelector from 'hooks/useAppSelector';
import useBreakPoints from 'hooks/useBreakPoints';
import useOnClickOutside from 'hooks/useCloseOnClickOutside';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { selectRemainingBudget, selectShowBudgetToUser } from 'store/reducers/cartSlice';
import styled from 'styled-components';
import CartButton from './CartButton';
import FilesNavigation from './FilesNavigation';
import HeaderIconButton from './HeaderIconButton';

const MainNavigation = ({ loginLabel, logoutLabel }: { loginLabel: string; logoutLabel: string }) => {
    const { info, files, theme, unit } = useAppSelector(({ shop }) => ({
        ...shop,
        files: shop?.shop?.files ?? [],
        unit: shop?.shop?.selectedUnit,
    }));

    const remainingBudget = useAppSelector(selectRemainingBudget);
    const showBudgetToUser = useAppSelector(selectShowBudgetToUser);
    const bp = useBreakPoints();
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const ref = useOnClickOutside(() => setShowMobileSearch(false), showMobileSearch);

    useEffect(() => {
        if (bp.md) {
            setShowMobileSearch(false);
        }
    }, [bp.md]);

    return (
        <StyledNavigationWrapper column>
            <StyledMainNavigation $bgColor={`${theme.main}`} $color={`${theme.mainTextColor}`}>
                <StyledNavigation className="w-100 px-3 align-items-center">
                    <NavContent>
                        <Flex className="me-4" align="center" justify="start">
                            {info?.logoType ? (
                                <Link to="/">
                                    <StyledLogo
                                        className="m-0"
                                        alt={unit?.name ?? info?.name ?? ''}
                                        src={info?.logoType ?? ''}
                                        height="45"
                                    />
                                </Link>
                            ) : (
                                <NavigationItem>
                                    <NavigationLink to="/">{unit?.name ?? info?.name}</NavigationLink>
                                </NavigationItem>
                            )}
                        </Flex>

                        {!bp.mdBelow && (
                            <SearchContainer className="w-100">
                                <Search />
                            </SearchContainer>
                        )}

                        <Flex className="align-items-center" justify="end" $gap={10}>
                            {showBudgetToUser && bp?.sm && (
                                <Flex align="center" $gap={8}>
                                    <FontAwesomeIcon icon={faCoins} /> <div>{remainingBudget}</div>
                                </Flex>
                            )}
                            <FilesNavigation files={files} direction="right" />
                            {bp.mdBelow && (
                                <HeaderIconButton small icon={faSearch} onClick={() => setShowMobileSearch(true)} />
                            )}

                            <Link to="/cart">
                                <CartButton className="nav-link" />
                            </Link>
                            {(info?.securityType ?? 0) > 1 && (
                                <StyledLoginMenu
                                    loginLabel={loginLabel}
                                    logoutLabel={logoutLabel}
                                    className="nav-link"
                                />
                            )}
                        </Flex>
                    </NavContent>

                    {bp.mdBelow && (
                        <MobileSearchOverlay active={showMobileSearch} ref={ref}>
                            <MobileSearchContainer>
                                <Search
                                    autoFocus={showMobileSearch}
                                    closeSearch={() => setShowMobileSearch(!showMobileSearch)}
                                />
                            </MobileSearchContainer>
                        </MobileSearchOverlay>
                    )}
                </StyledNavigation>
            </StyledMainNavigation>
        </StyledNavigationWrapper>
    );
};

const NavigationItem = styled.div`
    font-size: 18px;
    color: ${({ theme }) => theme.mainTextColor};
    cursor: default;
    padding-top: 2px;
    border-bottom: 2px solid transparent;
    &:hover {
        border-bottom-color: ${({ theme }) => theme.colors.mainText};
    }
`;

const NavigationLink = styled(Link)`
    font-size: 18px;
    color: ${({ theme }) => theme.mainTextColor};
    :hover,
    :focus,
    :visited {
        color: ${({ theme }) => theme.mainTextColor};
        text-decoration: none;
    }
`;

const StyledNavigationWrapper = styled(Flex)`
    background-color: rgb(244, 244, 244);
    z-index: 999;
    position: sticky;
    top: 0;
    flex-wrap: nowrap;
    justify-content: flex-start;
`;

const StyledMainNavigation = styled.div<{ $bgColor: string; $color: string }>`
    ${({ $bgColor }) => $bgColor && `background-color: ${$bgColor};`}
    ${({ $color }) => $color && `color: ${$color};`}
    height: 71px;
    width: 100%;
`;

const StyledLogo = styled.img`
    letter-spacing: 1.5px;
    margin-right: 1rem;
    text-transform: uppercase;
`;

const StyledNavigation = styled.div`
    max-width: ${({ theme }) => theme.breakpoints.xl}px;
    height: 100%;
    margin: 0px auto;
    position: relative;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
        overflow: visible;
    }
`;

const NavContent = styled.div`
    display: grid;
    width: 100%;
    height: 100%;
    grid-template-columns: 1fr 2fr 1fr;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
        grid-template-columns: 1fr 2fr 1fr;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
        grid-template-columns: auto 1fr auto;
    }
`;

const MobileSearchOverlay = styled.div<{ active: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.main};
    display: flex;
    align-items: center;
    opacity: ${(props) => (props.active ? 1 : 0)};
    visibility: ${(props) => (props.active ? 'visible' : 'hidden')};
    transition: opacity 0.2s ease, visibility 0.2s ease;
    z-index: 10;
`;

const MobileSearchContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 16px;
`;

const CloseButton = styled(IconButton)`
    color: ${({ theme }) => theme.mainTextColor};
`;

const SearchContainer = styled.div`
    width: 100%;
    max-width: 800px;
    justify-self: center;
    align-self: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
        max-width: 800px;
    }
`;

const StyledLoginMenu = styled(LoginMenu)`
    letter-spacing: 1.5px;
    text-transform: uppercase;
`;

export default MainNavigation;
